import React, { useState, useEffect } from 'react';

interface ReschedulePayload {
  date: string;
  unscheduled: boolean;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReschedule: (payload: ReschedulePayload) => void;
  taskTitle: string;
  currentDate: string;
  currentHour?: number;
  currentStartTime?: string;
  currentEndTime?: string;
}

const parseTime = (t?: string): { h: number; m: number } | null => {
  if (!t) return null;
  const parts = t.split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  if (isNaN(h)) return null;
  return { h, m };
};

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  isOpen,
  onClose,
  onReschedule,
  taskTitle,
  currentDate,
  currentHour,
  currentStartTime,
  currentEndTime
}) => {
  const [selectedDate, setSelectedDate] = useState(currentDate || new Date().toISOString().split('T')[0]);
  const [startHour, setStartHour] = useState<number>(currentHour ?? 9);
  const [startMinute, setStartMinute] = useState<number>(0);
  const [endHour, setEndHour] = useState<number>((currentHour ?? 9) + 1);
  const [endMinute, setEndMinute] = useState<number>(0);
  const [scheduleType, setScheduleType] = useState<'unscheduled' | 'timed'>('timed');

  // Sync form when a different task is opened
  useEffect(() => {
    if (!isOpen) return;
    const s = parseTime(currentStartTime);
    const e = parseTime(currentEndTime);
    const sh = s ? s.h : (currentHour ?? 9);
    const sm = s ? s.m : 0;
    setSelectedDate(currentDate || new Date().toISOString().split('T')[0]);
    setStartHour(sh);
    setStartMinute(sm);
    if (e && (e.h * 60 + e.m) > (sh * 60 + sm)) {
      setEndHour(e.h);
      setEndMinute(e.m);
    } else {
      setEndHour(sh + 1 > 23 ? 23 : sh + 1);
      setEndMinute(sm);
    }
    setScheduleType('timed');
  }, [isOpen, currentStartTime, currentEndTime, currentHour, currentDate]);

  if (!isOpen) return null;

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 15, 30, 45];

  const formatHour = (hour: number) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const startTotal = startHour * 60 + startMinute;
  const endTotal = endHour * 60 + endMinute;
  const durationMin = endTotal - startTotal;
  const durationValid = durationMin >= 15;
  const durationLabel = durationValid
    ? `${Math.floor(durationMin / 60) > 0 ? `${Math.floor(durationMin / 60)}h ` : ''}${durationMin % 60 > 0 ? `${durationMin % 60}m` : ''}`.trim()
    : 'End must be after start';

  const handleSubmit = () => {
    if (scheduleType === 'unscheduled') {
      onReschedule({ date: selectedDate, unscheduled: true, startHour, startMinute, endHour, endMinute });
      return;
    }
    if (!durationValid) return;
    onReschedule({ date: selectedDate, unscheduled: false, startHour, startMinute, endHour, endMinute });
  };

  const getQuickDates = () => {
    const today = new Date();
    const dates: { label: string; date: string }[] = [];
    dates.push({ label: 'Today', date: today.toISOString().split('T')[0] });
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    dates.push({ label: 'Tomorrow', date: tomorrow.toISOString().split('T')[0] });
    const nextWeek = new Date(today); nextWeek.setDate(today.getDate() + 7);
    dates.push({ label: 'Next Week', date: nextWeek.toISOString().split('T')[0] });
    const nextMonth = new Date(today); nextMonth.setMonth(today.getMonth() + 1);
    dates.push({ label: 'Next Month', date: nextMonth.toISOString().split('T')[0] });
    return dates;
  };

  const selectStyle: React.CSSProperties = {
    width: '100%', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem'
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', marginBottom: '6px'
  };

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 10000
      }}
      onClick={onClose}
      data-testid="reschedule-modal"
    >
      <div
        style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '24px',
          width: '440px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1f2937' }}>Move Task</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#9ca3af', cursor: 'pointer' }}>×</button>
          </div>
          <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Reschedule: <strong>{taskTitle}</strong>
          </p>
        </div>

        {/* Quick Date Buttons */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Quick Select</label>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {getQuickDates().map((item) => (
              <button
                key={item.date}
                onClick={() => setSelectedDate(item.date)}
                data-testid={`quick-date-${item.label.toLowerCase().replace(' ', '-')}`}
                style={{
                  padding: '8px 12px', borderRadius: '8px',
                  border: selectedDate === item.date ? '2px solid #667eea' : '1px solid #e5e7eb',
                  backgroundColor: selectedDate === item.date ? '#eff6ff' : 'white',
                  color: selectedDate === item.date ? '#667eea' : '#374151',
                  fontSize: '0.8rem', fontWeight: selectedDate === item.date ? 600 : 400,
                  cursor: 'pointer', transition: 'all 0.15s'
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date Picker */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Or Choose Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            data-testid="reschedule-date-input"
            style={{ width: '100%', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem', boxSizing: 'border-box' }}
          />
        </div>

        {/* Schedule Type */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Time</label>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setScheduleType('timed')}
              data-testid="reschedule-type-timed"
              style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                border: scheduleType === 'timed' ? '2px solid #667eea' : '1px solid #e5e7eb',
                backgroundColor: scheduleType === 'timed' ? '#eff6ff' : 'white',
                color: scheduleType === 'timed' ? '#667eea' : '#374151',
                fontWeight: scheduleType === 'timed' ? 600 : 400, cursor: 'pointer'
              }}
            >
              Set Time
            </button>
            <button
              onClick={() => setScheduleType('unscheduled')}
              data-testid="reschedule-type-unscheduled"
              style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                border: scheduleType === 'unscheduled' ? '2px solid #667eea' : '1px solid #e5e7eb',
                backgroundColor: scheduleType === 'unscheduled' ? '#eff6ff' : 'white',
                color: scheduleType === 'unscheduled' ? '#667eea' : '#374151',
                fontWeight: scheduleType === 'unscheduled' ? 600 : 400, cursor: 'pointer'
              }}
            >
              No Time (Flexible)
            </button>
          </div>
        </div>

        {/* Time Selection with minute precision */}
        {scheduleType === 'timed' && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Start Time</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <select
                    value={startHour}
                    data-testid="reschedule-start-hour"
                    onChange={(e) => {
                      const h = parseInt(e.target.value, 10);
                      setStartHour(h);
                      if (h * 60 + startMinute >= endHour * 60 + endMinute) {
                        const ne = h * 60 + startMinute + 60;
                        setEndHour(Math.min(23, Math.floor(ne / 60)));
                        setEndMinute(ne % 60);
                      }
                    }}
                    style={selectStyle}
                  >
                    {hours.map((h) => <option key={h} value={h}>{formatHour(h)}</option>)}
                  </select>
                  <select
                    value={startMinute}
                    data-testid="reschedule-start-minute"
                    onChange={(e) => setStartMinute(parseInt(e.target.value, 10))}
                    style={{ ...selectStyle, width: '90px' }}
                  >
                    {minutes.map((m) => <option key={m} value={m}>:{m.toString().padStart(2, '0')}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>End Time</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <select
                    value={endHour}
                    data-testid="reschedule-end-hour"
                    onChange={(e) => setEndHour(parseInt(e.target.value, 10))}
                    style={selectStyle}
                  >
                    {hours.map((h) => <option key={h} value={h}>{formatHour(h)}</option>)}
                  </select>
                  <select
                    value={endMinute}
                    data-testid="reschedule-end-minute"
                    onChange={(e) => setEndMinute(parseInt(e.target.value, 10))}
                    style={{ ...selectStyle, width: '90px' }}
                  >
                    {minutes.map((m) => <option key={m} value={m}>:{m.toString().padStart(2, '0')}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div style={{
              marginTop: '10px', fontSize: '0.8rem', fontWeight: 600,
              color: durationValid ? '#059669' : '#dc2626'
            }} data-testid="reschedule-duration">
              {durationValid ? `Duration: ${durationLabel}` : durationLabel}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={scheduleType === 'timed' && !durationValid}
            data-testid="reschedule-submit"
            style={{
              flex: 1, padding: '12px', borderRadius: '8px', border: 'none',
              backgroundColor: (scheduleType === 'timed' && !durationValid) ? '#c7d2fe' : '#667eea',
              color: 'white', fontWeight: 600,
              cursor: (scheduleType === 'timed' && !durationValid) ? 'not-allowed' : 'pointer'
            }}
          >
            Move Task
          </button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleModal;
